// Review step: the AI-filled AA form, grouped by gov-form section, fully
// editable, with per-field confidence + transcript provenance. Download fills
// the real gov PDF client-side.
//
// Component idiom: the KareHero MUI design system (lib/llama theme/mui - see
// the mui-stories Storybook). Plain MUI components styled by the branded
// theme, plus Vez's pwa DialogWithConfirmActions for the download gate.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { DialogWithConfirmActions } from '@karehero/llama/pwa/atoms/DialogWithConfirmActions/DialogWithConfirmActions';
import { useClaimFlow } from './ClaimFlowContext';
import { SAMPLE_CLIENT } from './fixtures';
import {
  sections,
  fieldsById,
  interpolate,
  isFieldActive,
  type FlatField,
} from './lib/aaSchema';
import type { AaAnswerValue, AnswerValues, FieldAnswer } from './lib/types';
import { downloadFilledAaPdf } from './lib/fillAaPdf';

const CONFIDENCE_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  high: 'success',
  medium: 'warning',
  low: 'default',
};

function ConfidenceTag({ fa }: { fa?: FieldAnswer }) {
  if (!fa || fa.value == null) return null;
  if (fa.edited)
    return <Chip size='small' label='edited' color='info' variant='outlined' />;
  const quotes = fa.sourceQuotes ?? [];
  return (
    <Tooltip
      arrow
      title={
        quotes.length ? (
          <Box>
            <Typography sx={{ fontSize: 12, mb: 0.5 }}>
              From the call:
            </Typography>
            {quotes.map((quoteText, i) => (
              <Typography key={i} sx={{ fontSize: 12, fontStyle: 'italic' }}>
                “{quoteText}”
              </Typography>
            ))}
          </Box>
        ) : (
          'AI-filled'
        )
      }
    >
      <Chip
        size='small'
        label={fa.confidence}
        color={CONFIDENCE_COLOR[fa.confidence]}
        variant='outlined'
      />
    </Tooltip>
  );
}

/** Long free-text "tell us more" boxes get a multiline editor. */
function isNarrative(field: FlatField): boolean {
  return field.id.endsWith('details') || field.kind === 'textarea';
}

function FieldEditor({
  field,
  error = false,
}: {
  field: FlatField;
  error?: boolean;
}) {
  const { answers, editAnswer } = useClaimFlow();
  const fa = answers[field.id];
  const value = fa?.value ?? null;
  const set = (v: AaAnswerValue | null) => editAnswer(field.id, v);

  switch (field.kind) {
    case 'boolean': {
      const v = value === true ? 'yes' : value === false ? 'no' : '';
      return (
        <ToggleButtonGroup
          size='small'
          exclusive
          value={v}
          onChange={(_, nv) => nv && set(nv === 'yes')}
          sx={
            error
              ? {
                  '& .MuiToggleButton-root': {
                    borderColor: 'error.main',
                    color: 'error.main',
                  },
                }
              : undefined
          }
        >
          <ToggleButton value='yes'>Yes</ToggleButton>
          <ToggleButton value='no'>No</ToggleButton>
        </ToggleButtonGroup>
      );
    }
    case 'select':
      return (
        <Select
          size='small'
          fullWidth
          displayEmpty
          error={error}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => set(e.target.value || null)}
        >
          <MenuItem value=''>
            <em>Not set</em>
          </MenuItem>
          {(field.options ?? []).map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      );
    case 'multiselect': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <FormGroup>
          {(field.options ?? []).map((o) => (
            <FormControlLabel
              key={o.id}
              control={
                <Checkbox
                  size='small'
                  checked={arr.includes(o.id)}
                  onChange={(e) =>
                    set(
                      e.target.checked
                        ? [...arr, o.id]
                        : arr.filter((x) => x !== o.id),
                    )
                  }
                />
              }
              label={<Typography sx={{ fontSize: 14 }}>{o.label}</Typography>}
            />
          ))}
        </FormGroup>
      );
    }
    case 'date':
      return (
        <TextField
          size='small'
          type='date'
          error={error}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => set(e.target.value || null)}
        />
      );
    case 'number':
      return (
        <TextField
          size='small'
          type='number'
          error={error}
          sx={{ maxWidth: 140 }}
          value={
            typeof value === 'number' || typeof value === 'string' ? value : ''
          }
          onChange={(e) =>
            set(e.target.value === '' ? null : Number(e.target.value))
          }
        />
      );
    case 'address': {
      const a = (
        value && typeof value === 'object' && !Array.isArray(value) ? value : {}
      ) as Record<string, string>;
      const keys: [string, string][] = [
        ['addressLine1', 'Address line 1'],
        ['addressLine2', 'Address line 2'],
        ['city', 'Town / city'],
        ['postalCode', 'Postcode'],
      ];
      return (
        <Stack spacing={1}>
          {keys.map(([k, lbl]) => (
            <TextField
              key={k}
              size='small'
              label={lbl}
              error={error}
              value={a[k] ?? ''}
              onChange={(e) => set({ ...a, [k]: e.target.value })}
            />
          ))}
        </Stack>
      );
    }
    case 'table': {
      const rows = (Array.isArray(value) ? value : []) as Record<
        string,
        string
      >[];
      const cols = field.columns ?? [];
      const update = (rowIdx: number, colId: string, v: string) => {
        const next = rows.map((r, i) =>
          i === rowIdx ? { ...r, [colId]: v } : r,
        );
        set(next);
      };
      return (
        <Stack spacing={2}>
          {rows.map((row, i) => (
            <Paper key={i} variant='outlined' sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                {cols.map((c) => (
                  <TextField
                    key={c.id}
                    size='small'
                    label={c.label}
                    value={row[c.id] ?? ''}
                    onChange={(e) => update(i, c.id, e.target.value)}
                  />
                ))}
              </Stack>
            </Paper>
          ))}
          <Button
            size='small'
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => set([...rows, {}])}
          >
            + Add row
          </Button>
        </Stack>
      );
    }
    default:
      return (
        <TextField
          size='small'
          fullWidth
          error={error}
          multiline={isNarrative(field)}
          minRows={isNarrative(field) ? 3 : undefined}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => set(e.target.value || null)}
        />
      );
  }
}

export default function Review() {
  const navigate = useNavigate();
  const { answers, reset } = useClaimFlow();
  const [snack, setSnack] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const getAnswerValue = (id: string) => answers[id]?.value ?? null;

  const filledCount = useMemo(
    () =>
      Object.values(answers).filter((a) => a.value != null && a.value !== '')
        .length,
    [answers],
  );

  // Show every ACTIVE field: depth-0 always, sub-questions whenever their
  // parent's answer triggers them (answered or not - the adviser must see
  // required sub-questions the call didn't cover). Untriggered sub-questions
  // are hidden and excluded from the PDF, so a flipped parent can't leave
  // contradictory detail answers behind.
  const visibleSections = useMemo(
    () =>
      sections
        .map((s) => ({
          ...s,
          fields: s.fields.filter(
            (f) =>
              f.depth === 0 || isFieldActive(f, fieldsById, getAnswerValue),
          ),
        }))
        .filter((s) => s.fields.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [answers],
  );

  // Per-section flags: AI answers worth double-checking + required fields still empty.
  const sectionStats = useMemo(() => {
    const stats: Record<string, { low: number; requiredEmpty: number }> = {};
    for (const s of visibleSections) {
      let low = 0;
      let requiredEmpty = 0;
      for (const f of s.fields) {
        const fa = answers[f.id];
        const isEmpty = fa?.value == null || fa.value === '';
        if (!isEmpty && fa && !fa.edited && fa.confidence === 'low') low += 1;
        if (f.required && isEmpty) requiredEmpty += 1;
      }
      stats[s.key] = { low, requiredEmpty };
    }
    return stats;
  }, [visibleSections, answers]);

  const lowTotal = useMemo(
    () => Object.values(sectionStats).reduce((n, s) => n + s.low, 0),
    [sectionStats],
  );
  const requiredEmptyTotal = useMemo(
    () => Object.values(sectionStats).reduce((n, s) => n + s.requiredEmpty, 0),
    [sectionStats],
  );

  const doDownload = async () => {
    setDownloading(true);
    try {
      // Only ACTIVE answers reach the PDF: a sub-answer whose parent trigger
      // no longer holds is dropped rather than filled contradictorily.
      const values: AnswerValues = {};
      for (const [qid, fa] of Object.entries(answers)) {
        if (fa.value == null || fa.value === '') continue;
        const field = fieldsById[qid];
        if (field && !isFieldActive(field, fieldsById, getAnswerValue))
          continue;
        values[qid] = fa.value;
      }
      const { filledCount: n } = await downloadFilledAaPdf(
        values,
        'attendance-allowance-arthur-whitmore.pdf',
      );
      setSnack(`Filled ${n} fields and downloaded the AA form.`);
    } catch (e) {
      setSnack(`Could not generate the PDF: ${(e as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  const onDownload = () => {
    // Gate the download while required fields are empty; the adviser can
    // choose to complete the gaps on paper and download anyway.
    if (requiredEmptyTotal > 0) {
      setGateOpen(true);
      return;
    }
    void doDownload();
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 8 }}>
      <AppBar position='sticky'>
        <Toolbar>
          <Box>
            <Typography>Review {SAMPLE_CLIENT.name}'s claim</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
              {filledCount} fields drafted from the call
              {lowTotal > 0 ? ` · ${lowTotal} need your check` : ''}
              {requiredEmptyTotal > 0
                ? ` · ${requiredEmptyTotal} required still empty`
                : ''}
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant='contained'
            color='secondary'
            disabled={downloading}
            onClick={onDownload}
          >
            {downloading ? 'Generating…' : 'Download filled form'}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth='md' sx={{ py: 3 }}>
        <Typography color='text.secondary' sx={{ mb: 2, fontSize: 14 }}>
          Each field shows how confident we are and the words from the call it
          came from. Edit anything before downloading. Fields the call didn't
          cover (like bank details) are left for you to complete.
        </Typography>

        {visibleSections.map((s) => (
          <Accordion
            key={s.key}
            defaultExpanded
            disableGutters
            sx={{ mb: 1, '&:before': { display: 'none' } }}
          >
            <AccordionSummary>
              <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
                <Typography>{s.label}</Typography>
                {(sectionStats[s.key]?.low ?? 0) > 0 && (
                  <Chip
                    size='small'
                    color='warning'
                    variant='outlined'
                    label={`${sectionStats[s.key].low} to check`}
                  />
                )}
                {(sectionStats[s.key]?.requiredEmpty ?? 0) > 0 && (
                  <Chip
                    size='small'
                    color='error'
                    variant='outlined'
                    label={`${sectionStats[s.key].requiredEmpty} required empty`}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2.5}>
                {s.fields.map((f) => {
                  const fa = answers[f.id];
                  const requiredEmpty =
                    f.required && (fa?.value == null || fa.value === '');
                  return (
                    <Box
                      key={f.id}
                      sx={{
                        pl: f.depth ? 2 : 0,
                        borderLeft: f.depth ? '2px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack
                        direction='row'
                        spacing={1}
                        sx={{ alignItems: 'center', mb: 0.5 }}
                      >
                        <Typography sx={{ fontSize: 14 }}>
                          {interpolate(f.label, SAMPLE_CLIENT.name)}
                        </Typography>
                        {requiredEmpty && (
                          <Chip size='small' color='error' label='Required' />
                        )}
                        <ConfidenceTag fa={fa} />
                      </Stack>
                      <FieldEditor field={f} error={requiredEmpty} />
                    </Box>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}

        <Stack direction='row' sx={{ justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant='text'
            onClick={() => {
              reset();
              navigate('/aa-claim');
            }}
          >
            Start over
          </Button>
          <Button
            variant='contained'
            disabled={downloading}
            onClick={onDownload}
          >
            {downloading ? 'Generating…' : 'Download filled form'}
          </Button>
        </Stack>
      </Container>

      <DialogWithConfirmActions
        title='Some required fields are empty'
        isOpen={gateOpen}
        onClose={() => setGateOpen(false)}
        actions={[
          {
            label: 'Keep editing',
            variant: 'outlined',
            onAction: () => setGateOpen(false),
          },
          {
            label: 'Download anyway',
            variant: 'contained',
            onAction: () => {
              setGateOpen(false);
              void doDownload();
            },
          },
        ]}
      >
        <Typography>
          The call didn't cover {requiredEmptyTotal} required field
          {requiredEmptyTotal === 1 ? '' : 's'} (things like bank details). You
          can fill them in here first, or download now and complete them by
          hand.
        </Typography>
      </DialogWithConfirmActions>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />
    </Box>
  );
}
